import { useCallback, useEffect, useMemo } from 'react';
import { driver, DriveStep, Config } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useTutorialStore } from '@/stores/tutorial';
import { useIntl } from 'react-intl';
import { useTenantID } from './useTenantID';
import { useAuth } from '@/stores/auth';
import { checkUserPermissions } from '@/components/Auth/checkUserPermissions';
import { useGetCurrentOrganization } from '@/api/trafikportalen';
import { useStudentsDisabled } from '@/contexts/ConfigContext';
import { useHasNonSchoolDepartmentAccess } from './useHasNonSchoolDepartmentAccess';

export const useTutorial = () => {
  const { formatMessage } = useIntl();
  const tenantId = useTenantID();
  const { userProfile } = useAuth();
  const {
    hasCompletedTutorial,
    setHasCompletedTutorial,
    setIsTutorialActive,
    isTutorialActive,
  } = useTutorialStore();

  const isTenantViewer = checkUserPermissions({ permission: 'tenant:viewer' });
  const { data: organization } = useGetCurrentOrganization({
    query: { enabled: !isTenantViewer },
  });
  const hasStudentCategories =
    (organization?.enabled_collection_types?.length ?? 0) > 0;

  const hasNonSchoolDepartmentAccess = useHasNonSchoolDepartmentAccess();
  const studentsDisabled = useStudentsDisabled();

  const organizationSteps: DriveStep[] = useMemo(() => {
    const steps: DriveStep[] = [
      {
        element: '[data-tutorial="dashboard"]',
        popover: {
          title: formatMessage({ defaultMessage: 'Välkommen!' }),
          description: formatMessage({
            defaultMessage:
              'Låt oss ta en snabb rundtur genom de viktigaste funktionerna.',
          }),
          side: 'bottom',
          align: 'start',
        },
      },
    ];

    if (hasNonSchoolDepartmentAccess) {
      steps.push(
        {
          element: '[data-tutorial="credit-info"]',
          popover: {
            title: formatMessage({ defaultMessage: 'Kreditinformation' }),
            description: formatMessage({
              defaultMessage:
                'Här på startsidan (och på andra ställen) ser du organisationens kreditgräns, vad som spenderats, tilldelats, samt tillgängligt saldo.',
            }),
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: '[data-tutorial="buy-ticket"]',
          popover: {
            title: formatMessage({ defaultMessage: 'Köp biljetter' }),
            description: formatMessage({
              defaultMessage:
                'Klicka här för att köpa nya biljetter för era resenärer.',
            }),
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: '[data-tutorial="new-traveller"]',
          popover: {
            title: formatMessage({ defaultMessage: 'Lägg till resenärer' }),
            description: formatMessage({
              defaultMessage:
                'Innan du kan köpa biljetter måste resenärer läggas till. Klicka här för att registrera en ny resenär.',
            }),
            side: 'bottom',
            align: 'start',
          },
        },
      );
    }

    steps.push(
      {
        element: '[data-tutorial="nav-tickets"]',
        popover: {
          title: formatMessage({ defaultMessage: 'Biljetter' }),
          description: formatMessage({
            defaultMessage:
              'Under denna sektion kan du se alla biljetter som köpts för organisationen.',
          }),
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-tutorial="nav-reports"]',
        popover: {
          title: formatMessage({ defaultMessage: 'Rapporter' }),
          description: formatMessage({
            defaultMessage:
              'Här hittar du rapporter över transaktioner och fakturaspecifikationer. Köp gjorda i appen synkas bara in en gång per månad.',
          }),
          side: 'right',
          align: 'start',
        },
      },
    );

    if (hasNonSchoolDepartmentAccess) {
      steps.push(
        {
          element: '[data-tutorial="nav-travellers"]',
          popover: {
            title: formatMessage({ defaultMessage: 'Resenärer' }),
            description: formatMessage({
              defaultMessage:
                'Hantera alla era resenärer här. Det går att lägga till, redigera och ta bort resenärer, samt hantera vilken eller vilka avdelningar de tillhör.',
            }),
            side: 'right',
            align: 'start',
          },
        },
        {
          element: '[data-tutorial="nav-departments"]',
          popover: {
            title: formatMessage({ defaultMessage: 'Organisation' }),
            description: formatMessage({
              defaultMessage:
                'Se och hantera er organisationsstruktur med avdelningar och underavdelningar. Ange faktureringsuppgifter här.',
            }),
            side: 'right',
            align: 'start',
          },
        },
      );
    }

    steps.push({
      element: '[data-tutorial="nav-administrators"]',
      popover: {
        title: formatMessage({ defaultMessage: 'Administratörer' }),
        description: formatMessage({
          defaultMessage:
            'Här hanteras vilka personer som har tillgång till organisationen och deras behörigheter.',
        }),
        side: 'right',
        align: 'start',
      },
    });

    if (hasStudentCategories && !studentsDisabled) {
      steps.push({
        element: '[data-tutorial^="nav-students"]',
        popover: {
          title: formatMessage({ defaultMessage: 'Elever' }),
          description: formatMessage({
            defaultMessage:
              'Här hanterar du elevkategorier som er organisation har aktiverade, till exempel skolskjuts, elevresor eller barn- och ungdomskort. Det finns en informationsflik med detaljerade instruktioner för hur du kommer igång.',
          }),
          side: 'right',
          align: 'start',
        },
      });
    }

    steps.push(
      {
        element: '[data-tutorial="profile-button"]',
        popover: {
          title: formatMessage({ defaultMessage: 'Dina inställningar' }),
          description: formatMessage({
            defaultMessage:
              'Här kan du ändra dina personliga inställningar, såsom lösenord och tillgänglighetsalternativ.',
          }),
          side: 'top',
          align: 'start',
        },
      },
      {
        popover: {
          title: formatMessage({ defaultMessage: 'Redo att börja!' }),
          description: formatMessage({
            defaultMessage:
              'Nu vet du grunderna! Börja med att skapa den organisationsstruktur som önskas, lägg till resenärer och sen är det bara att köpa biljetter och tilldela krediter. Om du behöver hjälp kan denna guide köras om igen från menyn.',
          }),
        },
      },
    );
    return steps;
  }, [
    formatMessage,
    hasNonSchoolDepartmentAccess,
    hasStudentCategories,
    studentsDisabled,
  ]);

  const tenantSteps: DriveStep[] = useMemo(() => {
    const steps: DriveStep[] = [
      {
        element: '[data-tutorial="dashboard"]',
        popover: {
          title: formatMessage({
            defaultMessage: 'Välkommen!',
          }),
          description: formatMessage({
            defaultMessage:
              'Som systemadministratör har du tillgång till alla funktioner som portalen har att erbjuda.',
          }),
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tutorial="nav-organizations"]',
        popover: {
          title: formatMessage({ defaultMessage: 'Organisationer' }),
          description: formatMessage({
            defaultMessage:
              'Hantera alla organisationer i systemet här, inklusive ansökningar från nya organisationer. Härifrån går det att "hoppa in i"/"Visa som" en organisation för att se mer detaljer och göra ändringar.',
          }),
          side: 'right',
          align: 'start',
        },
      },
    ];

    if (!studentsDisabled) {
      steps.push(
        {
          element: '[data-tutorial="nav-organizations"]',
          popover: {
            title: formatMessage({ defaultMessage: 'Elevhantering' }),
            description: formatMessage({
              defaultMessage:
                'Elevkategorier aktiveras per organisation under fliken Elevhantering. Där bestämmer du också om organisationen får hantera sina egna perioder, och vilka biljetter den får använda.',
            }),
            side: 'right',
            align: 'start',
          },
        },
        {
          element: '[data-tutorial="nav-periods"]',
          popover: {
            title: formatMessage({ defaultMessage: 'Perioder' }),
            description: formatMessage({
              defaultMessage:
                'Här skapar och ändrar du biljettperioder för organisationernas elevkategorier, för flera organisationer på en gång. Organisationer utan periodåtkomst kan se perioderna men inte ändra dem.',
            }),
            side: 'right',
            align: 'start',
          },
        },
        {
          element: '[data-tutorial="nav-periods"]',
          popover: {
            title: formatMessage({ defaultMessage: 'Perioder' }),
            description: formatMessage({
              defaultMessage:
                'En siffra på menyvalet visar hur många organisationer som saknar en aktiv eller kommande period. Utan en sådan period finns det inget som ger eleverna biljetter.',
            }),
            side: 'right',
            align: 'start',
          },
        },
      );
    }

    steps.push(
      {
        element: '[data-tutorial="nav-search"]',
        popover: {
          title: formatMessage({ defaultMessage: 'Sök resenärer' }),
          description: studentsDisabled
            ? formatMessage({
                defaultMessage:
                  'Sök på namn, e-post, personnummer, telefon eller kortnummer. Klicka på en träff för att öppna personen i dess organisation.',
              })
            : formatMessage({
                defaultMessage:
                  'Sök på namn, e-post, personnummer, telefon eller kortnummer, även bland elever. Klicka på en träff för att öppna personen i dess organisation.',
              }),
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-tutorial="nav-payers"]',
        popover: {
          title: formatMessage({ defaultMessage: 'Fakturamottagare' }),
          description: formatMessage({
            defaultMessage:
              'Hantera fakturamottagare och se organisationer med ofullständiga fakturauppgifter här. Varje gång en organisation skapar en ny fakturamottagare så måste den kompletteras av en systemadmin med information från ert ekonomisystem.',
          }),
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-tutorial="nav-payers"]',
        popover: {
          title: formatMessage({ defaultMessage: 'Fakturamottagare' }),
          description: formatMessage({
            defaultMessage:
              'SUPERVIKTIGT:Om inte alla fakturamottagare är kompletta vid månadsskiftet kommer månadsavstämningen att misslyckas och därmed faktureringen försenas.',
          }),
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-tutorial="nav-reports"]',
        popover: {
          title: formatMessage({ defaultMessage: 'Rapporter' }),
          description: formatMessage({
            defaultMessage:
              'Se systemövergripande rapporter över alla organisationer och transaktioner, inklusive fakturaspecifikationer.',
          }),
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-tutorial="nav-system-administrators"]',
        popover: {
          title: formatMessage({ defaultMessage: 'Systemadministratörer' }),
          description: formatMessage({
            defaultMessage:
              'Hantera systemadministratörer och deras behörigheter.',
          }),
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-tutorial="nav-settings"]',
        popover: {
          title: formatMessage({ defaultMessage: 'Inställningar' }),
          description: formatMessage({
            defaultMessage: 'Konfigurera systeminställningar.',
          }),
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-tutorial="profile-button"]',
        popover: {
          title: formatMessage({ defaultMessage: 'Dina inställningar' }),
          description: formatMessage({
            defaultMessage:
              'Här kan du ändra dina personliga inställningar, såsom lösenord, e-post och tillgänglighetsalternativ.',
          }),
          side: 'top',
          align: 'start',
        },
      },
      {
        element: '[data-tutorial="portal-info"]',
        popover: {
          title: formatMessage({ defaultMessage: 'Portalinformation' }),
          description: studentsDisabled
            ? formatMessage({
                defaultMessage:
                  'Här finns information om hur portalen fungerar som kan vara bra att veta.',
              })
            : formatMessage({
                defaultMessage:
                  'Här finns information om hur portalen fungerar som kan vara bra att veta, bland annat hur en organisation kommer igång med elever, hur perioder fungerar och vad elevernas biljettstatus betyder.',
              }),
          side: 'top',
          align: 'start',
        },
      },
      {
        popover: {
          title: formatMessage({
            defaultMessage: 'Redo att hantera systemet!',
          }),
          description: formatMessage({
            defaultMessage:
              'Nu har du fått en överblick av systemadministratörens funktioner. Du kan alltid starta om denna guide från menyn.',
          }),
        },
      },
    );
    return steps;
  }, [formatMessage, studentsDisabled]);

  const steps = useMemo(() => {
    if (isTenantViewer) {
      return tenantSteps;
    }
    return organizationSteps;
  }, [isTenantViewer, organizationSteps, tenantSteps]);

  const driverConfig: Config = useMemo(
    () => ({
      steps: steps,
      nextBtnText: formatMessage({ defaultMessage: 'Nästa' }),
      prevBtnText: formatMessage({ defaultMessage: 'Tillbaka' }),
      doneBtnText: formatMessage({ defaultMessage: 'Klar' }),
      overlayClickNext: false, // Don't advance on overlay click
      allowKeyboardControl: true,
      disableActiveInteraction: true,
      onDestroyed: () => {
        setIsTutorialActive(false);
        setHasCompletedTutorial(true);
      },
      onDeselected: () => {
        setIsTutorialActive(false);
      },
    }),
    [steps, formatMessage, setHasCompletedTutorial, setIsTutorialActive],
  );

  const startTutorial = useCallback(() => {
    const driverObj = driver(driverConfig);
    setIsTutorialActive(true);
    driverObj.drive();
  }, [driverConfig, setIsTutorialActive]);

  useEffect(() => {
    const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
    if (
      isDesktop &&
      !hasCompletedTutorial &&
      !isTutorialActive &&
      userProfile &&
      window.location.pathname === `/${tenantId}`
    ) {
      const timer = setTimeout(() => {
        startTutorial();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [
    hasCompletedTutorial,
    isTutorialActive,
    startTutorial,
    userProfile,
    tenantId,
  ]);

  return {
    startTutorial,
    hasCompletedTutorial,
    isTutorialActive,
  };
};
